import React, { useEffect, useState } from 'react';
import {
  Box, Button, Chip, Stack, TextField, Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import colors from '../../assets/theme/colors';
import lifelogIcon from '../../assets/image/logo/lifelog-handdrawn-32.png';

const MetadataLine = ({ label, value }) => value ? (
  <Typography sx={{ fontSize: '0.78rem', lineHeight: 1.45, color: alpha(colors.semantic.ink, 0.66) }}>
    <Box component="span" sx={{ fontWeight: 750 }}>{label}:</Box> {value}
  </Typography>
) : null;

const JourneyBriefHeader = ({ header, accent, editable = false, draft = false, onSave }) => {
  const [editing, setEditing] = useState(false);
  const [title, setTitle] = useState(header?.title || '');
  const [purpose, setPurpose] = useState(header?.purpose || '');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setTitle(header?.title || '');
    setPurpose(header?.purpose || '');
  }, [header?.purpose, header?.title]);

  const save = async () => {
    setSaving(true);
    const saved = await onSave?.({ title: title.trim(), purpose: purpose.trim() });
    setSaving(false);
    if (saved !== false) setEditing(false);
  };

  return (
    <Box sx={{ mb: 1.6, pb: 1.5, borderBottom: `1px solid ${alpha(accent.primary, 0.16)}` }}>
      <Stack direction="row" alignItems="center" spacing={0.65} sx={{ mb: 0.9 }}>
        <Box component="img" src={lifelogIcon} alt="" sx={{ width: 22, height: 22, objectFit: 'contain' }} />
        <Typography sx={{ fontSize: '0.72rem', fontWeight: 750, letterSpacing: '0.04em', color: accent.ink }}>
          Prepared with Lifelog
        </Typography>
        {draft ? <Chip label="Draft" size="small" sx={{ height: 22, fontSize: '0.66rem', fontWeight: 750 }} /> : null}
      </Stack>

      {editing ? (
        <Stack spacing={1}>
          <TextField label="Brief title" value={title} onChange={(event) => setTitle(event.target.value)} fullWidth />
          <TextField label="Purpose" value={purpose} onChange={(event) => setPurpose(event.target.value)} fullWidth multiline minRows={2} />
          <Stack direction="row" spacing={0.7}>
            <Button onClick={save} disabled={saving || !title.trim()} sx={{ textTransform: 'none', fontWeight: 750 }}>Save</Button>
            <Button onClick={() => { setTitle(header?.title || ''); setPurpose(header?.purpose || ''); setEditing(false); }} disabled={saving} sx={{ textTransform: 'none' }}>Cancel</Button>
          </Stack>
        </Stack>
      ) : (
        <>
          <Stack direction="row" alignItems="flex-start" spacing={0.8}>
            <Box sx={{ flex: 1 }}>
              <Typography component="h3" sx={{ fontSize: { xs: '1.25rem', md: '1.5rem' }, fontWeight: 800, lineHeight: 1.18, color: colors.semantic.ink }}>
                {header?.title}
              </Typography>
              {header?.purpose ? (
                <Typography sx={{ mt: 0.45, fontSize: '0.88rem', lineHeight: 1.5, color: alpha(colors.semantic.ink, 0.72) }}>
                  {header.purpose}
                </Typography>
              ) : null}
            </Box>
            {editable ? <Button onClick={() => setEditing(true)} sx={{ minWidth: 0, px: 0.8, textTransform: 'none', fontWeight: 750, color: accent.ink }}>Edit</Button> : null}
          </Stack>
          <Box sx={{ mt: 1 }}>
            <MetadataLine label="Prepared for" value={header?.personName} />
            <MetadataLine label="Prepared by" value={header?.preparedBy} />
            <MetadataLine label="Prepared on" value={header?.preparedOn} />
          </Box>
          <Typography sx={{ mt: 0.8, fontSize: '0.72rem', lineHeight: 1.45, color: alpha(colors.semantic.ink, 0.56) }}>
            {header?.trustStatement}
          </Typography>
        </>
      )}
    </Box>
  );
};

export default JourneyBriefHeader;
